import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthProvider.tsx';
import { thunkExtraArg } from '../../features/ui/store.tsx';
import { CriticalFailure } from '@cloudflare-monorepo/ui/components/CriticalFailure.tsx';
import { PageLoading } from '@cloudflare-monorepo/ui/components/PageLoading.tsx';
import { waitForMockDelay } from '@cloudflare-monorepo/ui/services/mock.ts';
import { Wrap } from '@cloudflare-monorepo/utils/errors';
import { useLogger } from '@cloudflare-monorepo/ui/providers/LoggerProvider.tsx';

import { createFetchClient } from '../../lib/kubb-fetch-client';
import { ApiClient as OverseerApiClient } from '../../generated/overseer/apiClient.ts';
import { ApiClient as AccountApiClient } from '../../generated/account/apiClient.ts';
import { ApiClient as UserApiClient } from '../../generated/users/apiClient.ts';
import { ApiClient as NotificationsApiClient } from '../../generated/notifications/apiClient.ts';
import { Logger } from '@cloudflare-monorepo/utils/logging/logger.ts';

export interface ApiClients {
  overseerApi: OverseerApiClient;
  accountApi: AccountApiClient;
  usersApi: UserApiClient;
  notificationsApi: NotificationsApiClient;
}

interface ApiClientContextType {
  clients: ApiClients | null;
}

export type AnyResponseError = Error & {
  response: Response;
};

export const AsResponseError = (err: unknown): AnyResponseError | null => {
  if (!(err instanceof Error)) {
    return null;
  }
  if ((err as AnyResponseError).response && (err as AnyResponseError).response instanceof Response) {
    const typed = err as AnyResponseError;
    return typed;
  }
  return null;
};

export const buildFetchClient = (logger: Logger) => {
  return createFetchClient({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    pre: [
      async (url, init) => {
        const urlToLog = url.startsWith(import.meta.env.VITE_API_BASE_URL) ? url.substring(import.meta.env.VITE_API_BASE_URL.length) : url;
        logger.info(`[API] executing ${init.method} ${urlToLog} at ${new Date().toISOString()}`);
        await waitForMockDelay();
        return { url, init: { ...init, redirect: 'manual' as RequestRedirect } };
      },
    ],
    post: [
      async (response) => {
        const { status } = response;
        if (status === 401) {
          logger.warn('Received 401 from API, reloading to re-authenticate');
          window.location.reload();
        } else if (status === 301 || status === 302) {
          const locationValue = response.headers.get('Location') ?? '';
          logger.warn('received redirection from API response (to ' + locationValue + ')');
          if (locationValue.includes('cloudflareaccess.com')) {
            logger.warn('CF Access session expired (redirect), reloading to re-authenticate');
            window.location.reload();
          }
        }
        return response;
      },
    ],
  });
};

const ApiClientContext = createContext<ApiClientContextType | undefined>(undefined);

export const ApiClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [contextApiClients, setContextApiClients] = useState<ApiClients | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const logger = useLogger('api-client');

  if (!isAuthenticated) {
    logger.error('this provider should never render when isAuthenticated is false');
  }
  if (!user) {
    logger.error('this provider should never render when user is missing');
  }
  if (isAuthLoading) {
    logger.error('this provider should never render when isAuthLoading is true');
  }

  useEffect(() => {
    if (!user || !isAuthenticated || isAuthLoading) {
      setContextApiClients(null);
      thunkExtraArg.apiClients = null;
      return;
    }

    if (contextApiClients) {
      if (!thunkExtraArg.apiClients) {
        thunkExtraArg.apiClients = contextApiClients;
      }
      return;
    }

    try {
      const fc = buildFetchClient(logger);

      const clients: ApiClients = {
        overseerApi: new OverseerApiClient(fc),
        accountApi: new AccountApiClient(fc),
        notificationsApi: new NotificationsApiClient(fc),
        usersApi: new UserApiClient(fc),
      };

      setContextApiClients(clients);
      thunkExtraArg.apiClients = clients;
    } catch (error) {
      logger.error('ApiClientProvider: Failed to create API clients', { error });
      setContextApiClients(null);
      thunkExtraArg.apiClients = null;
      setError(Wrap(error, "Non sono riuscito a collegarmi ai servizi dell'applicazione"));
    }
  }, [user, isAuthenticated, isAuthLoading]);

  return (
    <ApiClientContext.Provider value={{ clients: contextApiClients }}>
      {error ? (
        <CriticalFailure err={error} />
      ) : isAuthLoading || !contextApiClients || !user || !isAuthenticated ? (
        <PageLoading details="collegamento ai servizi" />
      ) : (
        children
      )}
    </ApiClientContext.Provider>
  );
};

export const useApiClient = (): ApiClients => {
  const context = useContext(ApiClientContext);
  if (!context) {
    throw new Error('useApiClient must be used within an ApiClientProvider');
  }
  if (!context.clients) {
    throw new Error('useApiClient must be protected against usage while loading at provider level');
  }
  return context.clients;
};
